import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, FileText, GraduationCap, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph, SITE, AUTHOR_REF } from "../lib/schema";

/**
 * Peer-reviewed publications page.
 *
 * Sources the `media` collection filtered to `type === "publication"`. Renders
 * an academic bibliography grouped by year (most recent first), each row
 * decorated with Schema.org `ScholarlyArticle` markup so Google Scholar +
 * search LLMs can resolve them back to Dr. Shantanu's `Physician` entity.
 */

// Parse a free-text date field ("Jul 2018", "2019", "Nov 2015") into a numeric
// year so we can group / sort. Falls back to a large sentinel for unparseable
// values so they sort last.
const yearFrom = (raw) => {
  if (!raw) return 0;
  const m = String(raw).match(/(19|20|21)\d{2}/);
  return m ? parseInt(m[0], 10) : 0;
};

// Extract the co-authors line from the summary text so we can display it as a
// distinct byline. Seed data uses the pattern:
//   "Co-authors: A, B, C — abstract text here."
// The em-dash sentinel avoids ambiguity with periods inside author initials
// like "Usha K." which would break a naive `.` split.
const splitAuthorsFromSummary = (summary = "") => {
  const m = summary.match(/^Co-authors:\s*(.+?)\s+[—–-]\s+(.+)$/s);
  if (m) return { authors: m[1].trim(), rest: m[2].trim() };
  // Fallback for legacy summaries with a period separator.
  const legacy = summary.match(/^Co-authors:\s*(.+?)\.\s+([A-Z].+)$/s);
  if (legacy) return { authors: legacy[1].trim(), rest: legacy[2].trim() };
  return { authors: "", rest: summary };
};

const Publications = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api
      .get("/media")
      .then((r) => setItems((r.data || []).filter((x) => x.type === "publication")))
      .catch(() => setItems([]));
  }, []);

  usePageMeta({
    title: "Peer-Reviewed Publications | Dr. Shantanu Kumar Gupta",
    path: "/publications",
    description:
      "Peer-reviewed research publications by Dr. Shantanu Kumar Gupta in ophthalmology — pediatric optical biometry, cataract surgery techniques, tonometry, endophthalmitis prevention. Indexed on ResearchGate.",
  });

  // JSON-LD: emit one ScholarlyArticle per row, all linked to the author's
  // @id so the knowledge graph joins publications ↔ physician entity.
  usePageJsonLd(
    useMemo(
      () =>
        graph([
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Publications", path: "/publications" },
          ]),
          {
            "@type": "CollectionPage",
            "@id": `${SITE}/publications#collection`,
            name: "Peer-Reviewed Publications — Dr. Shantanu Kumar Gupta",
            about: AUTHOR_REF,
            url: `${SITE}/publications`,
            hasPart: items.map((p) => ({
              "@type": "ScholarlyArticle",
              "@id": p.link,
              headline: p.title,
              name: p.title,
              url: p.link,
              datePublished: p.date,
              isPartOf: { "@type": "Periodical", name: p.outlet },
              author: AUTHOR_REF,
              abstract: p.summary,
            })),
          },
        ]),
      [items]
    )
  );

  // Group publications by year (desc) for a clean CV-style layout.
  const grouped = useMemo(() => {
    const g = {};
    for (const p of items) {
      const y = yearFrom(p.date) || 0;
      (g[y] = g[y] || []).push(p);
    }
    return Object.entries(g)
      .map(([year, list]) => [parseInt(year, 10), list])
      .sort((a, b) => b[0] - a[0]);
  }, [items]);

  const totalCount = items.length;

  return (
    <div data-testid="publications-page">
      {/* Hero */}
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 flex items-center gap-2">
          <GraduationCap size={14} strokeWidth={1.5} /> Peer-reviewed research
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Published, cited, indexed.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
          Selected peer-reviewed publications by Dr. Shantanu Kumar Gupta across paediatric ophthalmology, cataract surgery, tonometry and endophthalmitis prophylaxis. All papers are indexed on ResearchGate and cross-referenced to their host journals.
        </p>
        <div className="mt-6 flex items-center gap-4 flex-wrap">
          <a
            href="https://www.researchgate.net/profile/Shantanu-Gupta-4"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-xs"
            data-testid="publications-researchgate-btn"
          >
            View on ResearchGate <ExternalLink size={14} />
          </a>
          {totalCount > 0 && (
            <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              {totalCount} publication{totalCount === 1 ? "" : "s"} · 53 citations · 1,400+ reads
            </span>
          )}
        </div>
      </section>

      {/* Empty state */}
      {totalCount === 0 && (
        <section className="container-page pb-24 text-center text-muted-foreground text-sm">
          Publications are being catalogued. Please check back shortly, or browse the profile on ResearchGate above.
        </section>
      )}

      {/* Year-grouped list */}
      {grouped.map(([year, list]) => (
        <section key={year} className="py-10 container-page" data-testid={`publications-year-${year}`}>
          <div className="grid gap-8 lg:grid-cols-[180px_minmax(0,1fr)]">
            {/* Left: year rail */}
            <div>
              <div className="font-serif text-3xl lg:text-4xl text-[hsl(var(--primary))]">{year || "—"}</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mt-1">
                {list.length} paper{list.length === 1 ? "" : "s"}
              </div>
            </div>

            {/* Right: papers */}
            <div className="space-y-8 border-l border-border/60 pl-8 lg:pl-10">
              {list.map((p) => {
                const { authors, rest } = splitAuthorsFromSummary(p.summary);
                return (
                  <article
                    key={p.title}
                    data-testid={`publications-item-${p.title.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`}
                    className="group"
                  >
                    <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
                      <FileText size={12} />
                      <span>{p.outlet}</span>
                      {p.date && <span className="opacity-60">· {p.date}</span>}
                    </div>
                    <h3 className="font-serif text-xl sm:text-2xl mt-2 leading-snug">
                      <a
                        href={p.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-[hsl(var(--primary))] transition-colors inline-flex items-baseline gap-2"
                      >
                        {p.title}
                        <ArrowUpRight
                          size={16}
                          className="opacity-40 group-hover:opacity-100 group-hover:-translate-y-0.5 transition-all shrink-0"
                        />
                      </a>
                    </h3>
                    {authors && (
                      <div className="mt-2 text-sm text-[hsl(var(--foreground))]/80">
                        <span className="text-muted-foreground text-xs uppercase tracking-[0.14em] mr-2">Authors</span>
                        Dr. Shantanu Kumar Gupta, {authors}
                      </div>
                    )}
                    {rest && (
                      <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-3xl">{rest}</p>
                    )}

                    {/* Bibliographic identifiers — PMID / DOI / citation count.
                        Each is a live link out to the canonical source so search
                        engines, LLMs and journalists can verify the paper. */}
                    {(p.pmid || p.doi || (p.citations !== null && p.citations !== undefined && p.citations !== "")) && (
                      <div
                        className="mt-4 flex items-center gap-3 flex-wrap text-[11px]"
                        data-testid={`publications-meta-${p.title.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {p.pmid && (
                          <a
                            href={`https://pubmed.ncbi.nlm.nih.gov/${p.pmid}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-colors"
                          >
                            <span className="font-medium">PMID</span> {p.pmid}
                          </a>
                        )}
                        {p.doi && (
                          <a
                            href={`https://doi.org/${p.doi}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10 transition-colors"
                          >
                            <span className="font-medium">DOI</span> {p.doi}
                          </a>
                        )}
                        {typeof p.citations === "number" && p.citations > 0 && (
                          <a
                            href={p.semantic_scholar_id ? `https://www.semanticscholar.org/paper/${p.semantic_scholar_id}` : (p.doi ? `https://doi.org/${p.doi}` : p.link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 transition-colors font-medium"
                            title="Semantic Scholar citation count"
                          >
                            Cited by {p.citations}
                          </a>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Footer note */}
      {totalCount > 0 && (
        <section className="container-page py-16 border-t border-border/40 mt-6">
          <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
            For a full and continuously updated list, please visit Dr. Shantanu's{" "}
            <a
              href="https://www.researchgate.net/profile/Shantanu-Gupta-4"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(var(--primary))] hover:underline"
            >
              ResearchGate profile
            </a>
            . Citation counts and read metrics are maintained by ResearchGate.
          </p>
        </section>
      )}
    </div>
  );
};

export default Publications;
